import { useCallback, useEffect, useMemo, useRef } from 'react';
export const TRUNCATION_TOOLTIP_ATTR_NAME = 'data-sas-usetruncationtooltip';
export const UPDATE_TOOLTIP_EVENT_TYPE = 'sas.updatetooltip';

/**
 * @param element - HTMLElement to check
 * @param value - string value that is needed for elements like Inputs
 * @returns `true` or `false`, the `element` size < the `element` content or provided `value`
 */
export const isTruncated = (element, value) => {
  const buddy = document.createElement('span');
  if (element.innerHTML) {
    const children = element.childNodes;
    children.forEach(item => {
      const cln = item.cloneNode(true);
      buddy.appendChild(cln);
    });
  } else {
    // This is added for elements such as input
    buddy.textContent = value ?? getTextContent(element);
  }
  const elementStyles = getComputedStyle(element);
  const compareHeight = elementStyles.webkitLineClamp !== 'none';
  {
    buddy.style.position = 'absolute';
    buddy.style.visibility = 'hidden';
    buddy.style.top = '0';
    buddy.style.left = '0';
    buddy.style.whiteSpace = elementStyles.whiteSpace;
    buddy.style.fontFamily = elementStyles.fontFamily;
    buddy.style.fontSize = elementStyles.fontSize;
    buddy.style.fontWeight = elementStyles.fontWeight;
    buddy.style.fontVariant = elementStyles.fontVariant;
    buddy.style.paddingLeft = elementStyles.paddingLeft;
    buddy.style.paddingRight = elementStyles.paddingRight;
    buddy.style.borderLeftWidth = elementStyles.borderLeftWidth;
    buddy.style.borderRightWidth = elementStyles.borderRightWidth;
    buddy.style.borderLeftStyle = elementStyles.borderLeftStyle;
    buddy.style.borderRightStyle = elementStyles.borderRightStyle;
    if (compareHeight) {
      buddy.style.width = elementStyles.width;
      buddy.style.wordBreak = elementStyles.wordBreak;
    }
  }
  document.body.append(buddy);
  const contentSize = buddy.getBoundingClientRect()[compareHeight ? 'height' : 'width'].toFixed(2);
  const elementSize = element.getBoundingClientRect()[compareHeight ? 'height' : 'width'].toFixed(2);
  document.body.removeChild(buddy);
  return parseFloat(elementSize) < parseFloat(contentSize);
};
const getTextContent = element => {
  if (element?.tagName === 'INPUT') {
    const inputElement = element;
    return inputElement.value.length > 0 ? inputElement.value : inputElement.placeholder;
  }
  return element.textContent;
};
const setTooltip = (element, tooltip) => {
  const currentTooltip = element.title;
  if (tooltip !== currentTooltip) {
    element.title = tooltip;
  }
};

/**
 * Applies to a DOM ref, automatically makes its text content as a tooltip when truncates.
 * Please refer to the Link or Label  or select component for usage example.
 * @param tooltip - option to set custom tooltip
 * @param truncateRef - where the truncation will happen if it's different to where the tooltip will be set
 * @returns a callback to set the ref
 */

export const useTruncationTooltip = opts => {
  const ref = useRef(null);
  const tooltip = opts?.tooltip;
  const truncateRef = opts?.truncateRef;
  const mutationObserverConnected = useRef(false);
  const updateTooltip = useCallback(() => {
    const element = ref.current;
    if (element) {
      const truncateElement = truncateRef?.current ?? element;
      const content = getTextContent(truncateElement);
      if (content?.length && isTruncated(truncateElement)) {
        setTooltip(element, tooltip ? `${content}\n\n${tooltip}` : `${content}`);
      } else if (tooltip) {
        setTooltip(element, tooltip);
      } else {
        element.removeAttribute('title');
      }
    }
  }, [tooltip, truncateRef]);
  const mutationObserver = useMemo(() => {
    return new MutationObserver(updateTooltip);
  }, [updateTooltip]);
  const onFocusIn = useCallback(() => {
    if (!ref.current) return;
    updateTooltip();
    mutationObserver.disconnect();
    mutationObserver.observe(truncateRef?.current ?? ref.current, {
      attributeFilter: ['value'],
      subtree: true,
      characterData: true
    });
    mutationObserverConnected.current = true;
  }, [updateTooltip, mutationObserver, truncateRef]);
  const onFocusOut = useCallback(() => {
    mutationObserver.disconnect();
    mutationObserverConnected.current = false;
  }, [mutationObserver]);
  useEffect(() => {
    if (ref.current) {
      if (mutationObserverConnected.current) {
        updateTooltip();
        mutationObserver.observe(truncateRef?.current ?? ref.current, {
          attributeFilter: ['value'],
          subtree: true,
          characterData: true
        });
      }
    }
    return () => {
      if (mutationObserverConnected.current) {
        mutationObserver.disconnect();
      }
    };
  }, [mutationObserver, updateTooltip, tooltip, truncateRef]);
  return useCallback(instance => {
    if (instance === null) {
      ref.current?.removeEventListener('mouseenter', updateTooltip);
      ref.current?.removeEventListener('focusin', onFocusIn);
      ref.current?.removeEventListener('focusout', onFocusOut);
      ref.current?.removeEventListener(UPDATE_TOOLTIP_EVENT_TYPE, updateTooltip);
      ref.current?.removeAttribute(TRUNCATION_TOOLTIP_ATTR_NAME);
    } else {
      instance.addEventListener('mouseenter', updateTooltip);
      instance.addEventListener('focusin', onFocusIn);
      instance.addEventListener('focusout', onFocusOut);
      // others can trigger this event to force re-calculate trunction tooltip
      instance.addEventListener(UPDATE_TOOLTIP_EVENT_TYPE, updateTooltip);
      // let KeyboardTooltip know that this element's title is managed by this hook
      instance.setAttribute(TRUNCATION_TOOLTIP_ATTR_NAME, 'true');
    }
    ref.current = instance;
  }, [onFocusIn, onFocusOut, updateTooltip]);
};
//# sourceMappingURL=useTruncationTooltip.js.map